import { LitElement, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { marked } from 'marked';
import * as lifeplanApi from '../services/lifeplan-api.js';

@customElement('cp-page-lifeplan')
export class CpPageLifeplan extends LitElement {
  createRenderRoot() { return this; }

  @state() private _markdown = '';
  @state() private _loading = true;
  @state() private _saving = false;
  @state() private _error = '';
  @state() private _dirty = false;
  @state() private _configured = false;
  @state() private _previewHtml = '';

  private _debounceTimer: ReturnType<typeof setTimeout> | null = null;

  connectedCallback() {
    super.connectedCallback();
    this._loadContent();
    window.addEventListener('keydown', this._onKeyDown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('keydown', this._onKeyDown);
    if (this._debounceTimer) clearTimeout(this._debounceTimer);
  }

  private _onKeyDown = (e: KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 's') {
      e.preventDefault();
      if (this._dirty && !this._saving) this._save();
    }
  };

  private async _loadContent() {
    this._loading = true;
    this._error = '';

    try {
      const config = await lifeplanApi.getConfig();
      if ('isConfigured' in config && !config.isConfigured) {
        this._configured = false;
        this._loading = false;
        return;
      }
      this._configured = true;

      const { markdown } = await lifeplanApi.getContent();
      this._markdown = markdown;
      this._updatePreview(markdown);
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Laden';
    } finally {
      this._loading = false;
    }
  }

  private _onInput(e: InputEvent) {
    const textarea = e.target as HTMLTextAreaElement;
    this._markdown = textarea.value;
    this._dirty = true;

    if (this._debounceTimer) clearTimeout(this._debounceTimer);
    this._debounceTimer = setTimeout(() => {
      this._updatePreview(this._markdown);
    }, 150);
  }

  private _updatePreview(markdown: string) {
    this._previewHtml = marked.parse(markdown, { async: false }) as string;
  }

  private async _save() {
    if (!this._dirty || this._saving) return;

    this._saving = true;
    this._error = '';

    try {
      await lifeplanApi.saveContent(this._markdown);
      this._dirty = false;
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Speichern';
    } finally {
      this._saving = false;
    }
  }

  render() {
    return html`
      <div class="cp-page cp-page-lifeplan">
        <div class="cp-page-lifeplan__header">
          <h1 class="cp-page__title" style="margin:0">Lebensplan</h1>
          ${this._configured ? html`
            <div class="cp-page-lifeplan__actions">
              ${this._dirty ? html`<span class="cp-page-lifeplan__dirty-indicator"></span>` : ''}
              <button
                class="cp-page-lifeplan__save-btn"
                ?disabled=${!this._dirty || this._saving}
                @click=${this._save}
              >${this._saving ? 'Speichern...' : 'Speichern'}</button>
            </div>
          ` : ''}
        </div>

        ${this._error ? html`
          <div class="cp-page-lifeplan__error">
            <p class="cp-page-lifeplan__error-text">${this._error}</p>
          </div>
        ` : ''}

        ${this._loading ? html`
          <div class="cp-page-lifeplan__loading">
            <p class="cp-page-lifeplan__loading-text">Laden...</p>
          </div>
        ` : !this._configured ? html`
          <div class="cp-page-lifeplan__not-configured">
            <p class="cp-page-lifeplan__not-configured-text">
              Kein Lebensplan konfiguriert.
            </p>
            <a class="cp-page-lifeplan__settings-link" href="#/settings">
              In den Settings konfigurieren
            </a>
          </div>
        ` : html`
          <div class="cp-page-lifeplan__split">
            <div class="cp-page-lifeplan__editor">
              <textarea
                class="cp-page-lifeplan__textarea"
                .value=${this._markdown}
                @input=${this._onInput}
                spellcheck="false"
              ></textarea>
            </div>
            <div class="cp-page-lifeplan__preview">
              <div class="cp-page-lifeplan__preview-content" .innerHTML=${this._previewHtml}></div>
            </div>
          </div>
        `}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-page-lifeplan': CpPageLifeplan;
  }
}
